// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { billingFrequencySchema } from '@/constants/billing-frequency';
import { caseInsensitiveEnum } from '@/constants/case-insensitive-enum';
import { chargeModeSchema } from '@/constants/charge-mode';
import { chargeScheduleSchema } from '@/constants/charge-schedule';
import { chargeStatusSchema } from '@/constants/charge-status';
import { chargeTriggerTypeSchema } from '@/constants/charge-trigger-type';
import { invoiceModeSchema } from '@/constants/invoice-mode';
import { invoiceStatusSchema } from '@/constants/invoice-status';
import { pricingModeSchema } from '@/constants/pricing-mode';
import { currencySchema } from '@/currency/api/types';
import { isoCurrencyCodeSchema } from '@/currency/constants/iso';
import { sparseItemSchema, sparseProjectSchema } from '@/sparse/api/types';
import { dollarStringSchema } from '@/utilities/currency';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

export const CHARGE_CANCELLATION_REASON_MAX_LENGTH = 4096;

export const chargeSortPropertySchema = z.enum([
  'project',
  'customer',
  'label',
  'amount',
  'servicePeriodStart',
  'billDate',
  'status',
  'item',
]);

export type ChargeSortProperty = z.infer<typeof chargeSortPropertySchema>;

const chargeListItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  // The project carries the charge's customer.
  project: sparseProjectSchema,
  label: z.string(),
  splitIndex: z.number(),
  splitCount: z.number(),
  item: sparseItemSchema,
  quantity: z.number(),
  unitPrice: currencySchema.nullable(),
  totalPrice: currencySchema,
  pricingMode: pricingModeSchema,
  servicePeriodStart: dateSchema.nullable(),
  servicePeriodEnd: dateSchema.nullable(),
  expectedBillableDate: dateSchema.nullable(),
  actualBillableAt: dateTimeSchema.nullable(),
  status: chargeStatusSchema,
  triggerType: chargeTriggerTypeSchema,
  // Parent line item's chargeSchedule + chargeMode. The listing query
  // filters NULL-schedule line items, so chargeSchedule is non-null.
  // Mark-billable is gated on (lineItemChargeMode=PENDING && schedule
  // !=MILESTONE): a PERIODIC charge can only be billed once its plan is
  // activated; MILESTONE charges flow through because the action IS the
  // activation event.
  chargeSchedule: chargeScheduleSchema,
  lineItemChargeMode: chargeModeSchema,
  lineItemKey: z.string(),
  cancellationReason: z.string().nullable(),
  cancelledAt: dateTimeSchema.nullable(),
  invoiceKey: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
});

const chargeAmountRangeSchema = z.object({
  min: dollarStringSchema,
  max: dollarStringSchema,
});

export type ChargeListItem = z.infer<typeof chargeListItemSchema>;

export const chargeSchema = z.object({
  id: z.string(),
  key: z.string(),
  label: z.string(),
  pricingMode: pricingModeSchema,
  quantity: z.number(),
  unitPrice: currencySchema.nullable(),
  totalPrice: currencySchema,
  servicePeriodStart: dateSchema.nullable(),
  servicePeriodEnd: dateSchema.nullable(),
  expectedBillableDate: dateSchema.nullable(),
  billingEligibleDate: dateSchema.nullable(),
  status: chargeStatusSchema,
  isCustomModified: z.boolean(),
  triggerType: chargeTriggerTypeSchema,
  sequence: z.number(),
});

export type Charge = z.infer<typeof chargeSchema>;

// The status breakdown for a single total-price currency. There is no
// cross-currency total — each currency stacks independently on the cards.
const currencyChargeStatusSummarySchema = z.object({
  isoCode: z.preprocess(
    (v) => (typeof v === 'string' ? v.toUpperCase() : v),
    isoCurrencyCodeSchema
  ),
  plannedAmount: dollarStringSchema,
  plannedCount: z.number(),
  billableAmount: dollarStringSchema,
  billableCount: z.number(),
  invoicedAmount: dollarStringSchema,
  invoicedCount: z.number(),
  cancelledAmount: dollarStringSchema,
  cancelledCount: z.number(),
});

export type CurrencyChargeStatusSummary = z.infer<
  typeof currencyChargeStatusSummarySchema
>;

const chargeStatusSummarySchema = z.object({
  byCurrency: z
    .array(currencyChargeStatusSummarySchema)
    .nullable()
    .transform((v) => v ?? []),
});

export type ChargeStatusSummary = z.infer<typeof chargeStatusSummarySchema>;

export const chargeListingSchema = z.object({
  results: z
    .array(chargeListItemSchema)
    .nullable()
    .transform((v) => v ?? []),
  totalCount: z.number(),
  statusSummary: chargeStatusSummarySchema,
  amountRange: chargeAmountRangeSchema,
});

// ---------------------------------------------------------------------------
// Invoice listing
// ---------------------------------------------------------------------------

export const invoiceAgingBucketSchema = z.enum([
  'current',
  '1-30',
  '31-60',
  '61-90',
  '91+',
]);

export type InvoiceAgingBucket = z.infer<typeof invoiceAgingBucketSchema>;

export const invoiceSortPropertySchema = z.enum([
  'invoiceNumber',
  'customer',
  'project',
  'invoiceDate',
  'dueDate',
  'total',
  'balance',
  'status',
]);

export type InvoiceSortProperty = z.infer<typeof invoiceSortPropertySchema>;

export const invoiceLineItemSortPropertySchema = z.enum([
  'invoiceNumber',
  'customer',
  'project',
  'description',
  'item',
  'total',
  'servicePeriodStart',
  'invoiceDate',
  'sentDate',
  'paidDate',
  'status',
]);

export type InvoiceLineItemSortProperty = z.infer<
  typeof invoiceLineItemSortPropertySchema
>;

const invoiceListItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  invoiceNumber: z.string(),
  status: invoiceStatusSchema,
  customerKey: z.string().nullable(),
  customerName: z.string().nullable(),
  projectKey: z.string().nullable(),
  projectName: z.string().nullable(),
  invoiceDate: dateSchema,
  dueDate: dateSchema.nullable(),
  total: currencySchema,
  balance: currencySchema,
  currency: isoCurrencyCodeSchema,
  daysOverdue: z.number(),
  agingBucket: invoiceAgingBucketSchema,
  draftedAt: dateTimeSchema,
  openedAt: dateTimeSchema.nullable(),
  firstSentAt: dateTimeSchema.nullable(),
  lastSentAt: dateTimeSchema.nullable(),
  paidDate: dateSchema.nullable(),
  voidedAt: dateTimeSchema.nullable(),
});

export type InvoiceListItem = z.infer<typeof invoiceListItemSchema>;

const invoiceAmountRangeSchema = z.object({
  min: dollarStringSchema,
  max: dollarStringSchema,
});

// CurrencyInvoiceSummary is the MECE four-bucket breakdown for a single
// currency. Every non-void invoice maps to exactly one bucket so totals
// never double-count:
//   * Draft         — not yet issued (pre-AR)
//   * Current       — issued, not yet due, balance remaining
//   * Overdue       — issued, past due, balance remaining
//   * Paid (30d)    — paid within the trailing 30 days
// Current and Overdue both sit under the colloquial "open" umbrella but
// are mutually exclusive along the due-date axis. Clients can derive
// "total outstanding" as currentAmount + overdueAmount within a currency.
const currencyInvoiceSummarySchema = z.object({
  isoCode: z.preprocess(
    (v) => (typeof v === 'string' ? v.toUpperCase() : v),
    isoCurrencyCodeSchema
  ),
  draftAmount: dollarStringSchema,
  draftCount: z.number(),
  currentAmount: dollarStringSchema,
  currentCount: z.number(),
  overdueAmount: dollarStringSchema,
  overdueCount: z.number(),
  paidRecentAmount: dollarStringSchema,
  paidRecentCount: z.number(),
});

export type CurrencyInvoiceSummary = z.infer<
  typeof currencyInvoiceSummarySchema
>;

// InvoiceSummary buckets the four-state breakdown per currency. There is
// no cross-currency total — each currency stacks independently.
const invoiceSummarySchema = z.object({
  byCurrency: z
    .array(currencyInvoiceSummarySchema)
    .nullable()
    .transform((v) => v ?? []),
});

export type InvoiceSummary = z.infer<typeof invoiceSummarySchema>;

export const invoiceListingSchema = z.object({
  results: z
    .array(invoiceListItemSchema)
    .nullable()
    .transform((v) => v ?? []),
  totalCount: z.number(),
  summary: invoiceSummarySchema,
  amountRange: invoiceAmountRangeSchema,
});

const invoiceLineItemListItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  invoiceKey: z.string(),
  invoiceNumber: z.string(),
  invoiceStatus: invoiceStatusSchema,
  currency: z.preprocess(
    (v) => (typeof v === 'string' ? v.toUpperCase() : v),
    isoCurrencyCodeSchema
  ),
  invoiceDate: dateSchema,
  dueDate: dateSchema.nullable(),
  firstSentAt: dateTimeSchema.nullable(),
  paidDate: dateSchema.nullable(),
  customerKey: z.string().nullable(),
  customerName: z.string().nullable(),
  projectKey: z.string().nullable(),
  projectName: z.string().nullable(),
  description: z.string(),
  category: z.string().nullable(),
  pricingMode: pricingModeSchema,
  quantity: z.number(),
  unitPrice: currencySchema.nullable(),
  totalPrice: currencySchema,
  taxAmount: currencySchema.nullable(),
  lineTotal: currencySchema,
  servicePeriodStart: dateSchema.nullable(),
  servicePeriodEnd: dateSchema.nullable(),
  billingEligibleDate: dateSchema.nullable(),
  expectedBillableDate: dateSchema.nullable(),
  itemKey: z.string().nullable(),
  itemName: z.string().nullable(),
  itemSku: z.string().nullable(),
});

export type InvoiceLineItemListItem = z.infer<
  typeof invoiceLineItemListItemSchema
>;

const currencyInvoiceLineItemSummarySchema = z.object({
  isoCode: z.preprocess(
    (v) => (typeof v === 'string' ? v.toUpperCase() : v),
    isoCurrencyCodeSchema
  ),
  lineItemCount: z.number(),
  invoiceCount: z.number(),
  subtotal: dollarStringSchema,
  taxTotal: dollarStringSchema,
  total: dollarStringSchema,
  postedAmount: dollarStringSchema,
  postedCount: z.number(),
  draftAmount: dollarStringSchema,
  draftCount: z.number(),
  voidAmount: dollarStringSchema,
  voidCount: z.number(),
});

export type CurrencyInvoiceLineItemSummary = z.infer<
  typeof currencyInvoiceLineItemSummarySchema
>;

// Buckets the report figures per currency. There is no cross-currency
// total — each currency stacks independently.
const invoiceLineItemReportSummarySchema = z.object({
  byCurrency: z
    .array(currencyInvoiceLineItemSummarySchema)
    .nullable()
    .transform((v) => v ?? []),
});

export const invoiceLineItemListingSchema = z.object({
  results: z
    .array(invoiceLineItemListItemSchema)
    .nullable()
    .transform((v) => v ?? []),
  totalCount: z.number(),
  summary: invoiceLineItemReportSummarySchema,
});

// ---------------------------------------------------------------------------
// Invoice detail
// ---------------------------------------------------------------------------

const invoiceBillFromSchema = z.object({
  name: z.string(),
  address: z.string().nullable(),
});

export type InvoiceBillFrom = z.infer<typeof invoiceBillFromSchema>;

const invoiceBillToSchema = z.object({
  companyName: z.string().nullable(),
  billingContactName: z.string().nullable(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
});

export type InvoiceBillTo = z.infer<typeof invoiceBillToSchema>;

const invoiceLineItemSchema = z.object({
  id: z.string(),
  key: z.string(),
  description: z.string(),
  category: z.string().nullable(),
  pricingMode: pricingModeSchema,
  quantity: z.number(),
  unitPrice: currencySchema.nullable(),
  totalPrice: currencySchema,
  taxAmount: currencySchema.nullable(),
  lineTotal: currencySchema,
  servicePeriodStart: dateSchema.nullable(),
  servicePeriodEnd: dateSchema.nullable(),
  sequence: z.number(),
});

export type InvoiceLineItem = z.infer<typeof invoiceLineItemSchema>;

const invoiceSettlementSourceTypeSchema = z.enum(['PAYMENT', 'CREDIT_MEMO']);

const invoiceSettlementSchema = z.object({
  id: z.string(),
  key: z.string(),
  sourceType: invoiceSettlementSourceTypeSchema,
  amount: currencySchema,
  appliedAt: dateTimeSchema,
  memo: z.string().nullable(),
  paymentKey: z.string().nullable(),
  paymentReference: z.string().nullable(),
  paymentDate: dateSchema.nullable(),
  creditMemoKey: z.string().nullable(),
  creditMemoNumber: z.string().nullable(),
});

export type InvoiceSettlement = z.infer<typeof invoiceSettlementSchema>;

export const invoiceDetailSchema = z.object({
  id: z.string(),
  key: z.string(),
  invoiceNumber: z.string(),
  status: invoiceStatusSchema,
  invoiceDate: dateSchema,
  dueDate: dateSchema.nullable(),
  subtotal: currencySchema,
  taxTotal: currencySchema,
  total: currencySchema,
  balance: currencySchema,
  currency: isoCurrencyCodeSchema,
  daysOverdue: z.number(),
  agingBucket: invoiceAgingBucketSchema,
  paymentTerms: z.string().nullable(),
  purchaseOrderNumber: z.string().nullable(),
  memo: z.string().nullable(),
  paymentInstructions: z.string().nullable(),
  notes: z.string().nullable(),
  billFrom: invoiceBillFromSchema,
  billTo: invoiceBillToSchema,
  customerKey: z.string().nullable(),
  customerName: z.string().nullable(),
  project: sparseProjectSchema.nullable(),
  draftedAt: dateTimeSchema,
  openedAt: dateTimeSchema.nullable(),
  firstSentAt: dateTimeSchema.nullable(),
  lastSentAt: dateTimeSchema.nullable(),
  paidDate: dateSchema.nullable(),
  voidedAt: dateTimeSchema.nullable(),
  voidReason: z.string().nullable(),
  lineItemCount: z.number(),
  settlementCount: z.number(),
  lineItems: z
    .array(invoiceLineItemSchema)
    .nullable()
    .transform((v) => v ?? []),
  settlements: z
    .array(invoiceSettlementSchema)
    .nullable()
    .transform((v) => v ?? []),
});

export type InvoiceDetail = z.infer<typeof invoiceDetailSchema>;

// ---------------------------------------------------------------------------
// Billing hub summary
// ---------------------------------------------------------------------------

// The charge totals for the billing hub, for a single total-price currency.
// readyToInvoiceAmount is the billable headline; the per-status amounts/counts
// drive the breakdown. There is no cross-currency total — each currency stacks
// independently on the card.
const currencyChargeSummarySchema = z.object({
  isoCode: z.preprocess(
    (v) => (typeof v === 'string' ? v.toUpperCase() : v),
    isoCurrencyCodeSchema
  ),
  readyToInvoiceAmount: dollarStringSchema,
  plannedAmount: dollarStringSchema,
  plannedCount: z.number(),
  billableAmount: dollarStringSchema,
  billableCount: z.number(),
  invoicedAmount: dollarStringSchema,
  invoicedCount: z.number(),
});

export type CurrencyChargeSummary = z.infer<typeof currencyChargeSummarySchema>;

const chargeSummarySchema = z.object({
  byCurrency: z
    .array(currencyChargeSummarySchema)
    .nullable()
    .transform((v) => v ?? []),
});

const billingPlanStatusSchema = caseInsensitiveEnum([
  'PENDING',
  'CONFIRMED',
  'ACTIVE',
  'COMPLETE',
]);

export type BillingPlanStatus = z.infer<typeof billingPlanStatusSchema>;

// The plan-status totals for the billing hub, for a single contract-value
// currency. There is no cross-currency total — each currency stacks
// independently on the card.
const currencyPlanSummarySchema = z.object({
  isoCode: z.preprocess(
    (v) => (typeof v === 'string' ? v.toUpperCase() : v),
    isoCurrencyCodeSchema
  ),
  pendingAmount: dollarStringSchema,
  pendingCount: z.number(),
  confirmedAmount: dollarStringSchema,
  confirmedCount: z.number(),
  activeAmount: dollarStringSchema,
  activeCount: z.number(),
});

export type CurrencyPlanSummary = z.infer<typeof currencyPlanSummarySchema>;

const billingPlanSummarySchema = z.object({
  byCurrency: z
    .array(currencyPlanSummarySchema)
    .nullable()
    .transform((v) => v ?? []),
});

export const billingSummarySchema = z.object({
  charges: chargeSummarySchema,
  invoices: invoiceSummarySchema,
  billingPlans: billingPlanSummarySchema,
});

export type BillingSummary = z.infer<typeof billingSummarySchema>;

// ---------------------------------------------------------------------------
// Billing plan listing (UX Surface 7)
// ---------------------------------------------------------------------------

export const billingPlanSortPropertySchema = z.enum([
  'project',
  'customer',
  'status',
  'activationDate',
  'chargeSchedule',
  'contractValue',
  'item',
]);

export type BillingPlanSortProperty = z.infer<
  typeof billingPlanSortPropertySchema
>;

const billingPlanListItemSchema = z.object({
  lineItemKey: z.string(),
  projectKey: z.string(),
  projectName: z.string(),
  customerKey: z.string().nullable(),
  customerName: z.string().nullable(),
  itemSku: z.string(),
  itemName: z.string(),
  quantity: z.number(),
  startOn: dateSchema.nullable(),
  endOn: dateSchema.nullable(),
  chargeSchedule: chargeScheduleSchema.nullable(),
  chargeFrequency: billingFrequencySchema.nullable(),
  invoiceFrequency: billingFrequencySchema.nullable(),
  invoiceMode: invoiceModeSchema.nullable(),
  pricingMode: pricingModeSchema.nullable(),
  unitPrice: currencySchema.nullable(),
  fixedPrice: currencySchema.nullable(),
  totalOverride: currencySchema.nullable(),
  chargeMode: chargeModeSchema,
  activationDate: dateSchema.nullable(),
  plannedCount: z.number(),
  billableCount: z.number(),
  invoicedCount: z.number(),
  cancelledCount: z.number(),
  contractValue: currencySchema,
  status: billingPlanStatusSchema,
  // Plan-level completion timestamp — populated only when status is
  // 'COMPLETE'. Null for any plan that hasn't been fully invoiced.
  completedOn: dateTimeSchema.nullable(),
});

export type BillingPlanListItem = z.infer<typeof billingPlanListItemSchema>;

const billingPlanContractValueRangeSchema = z.object({
  min: dollarStringSchema,
  max: dollarStringSchema,
});

// The plan status breakdown for a single contract-value currency. There is no
// cross-currency total — each currency stacks independently on the cards.
const currencyPlanStatusSummarySchema = z.object({
  isoCode: z.preprocess(
    (v) => (typeof v === 'string' ? v.toUpperCase() : v),
    isoCurrencyCodeSchema
  ),
  pendingAmount: dollarStringSchema,
  pendingCount: z.number(),
  confirmedAmount: dollarStringSchema,
  confirmedCount: z.number(),
  activeAmount: dollarStringSchema,
  activeCount: z.number(),
  completeAmount: dollarStringSchema,
  completeCount: z.number(),
});

export type CurrencyPlanStatusSummary = z.infer<
  typeof currencyPlanStatusSummarySchema
>;

const planStatusSummarySchema = z.object({
  byCurrency: z
    .array(currencyPlanStatusSummarySchema)
    .nullable()
    .transform((v) => v ?? []),
});

export type PlanStatusSummary = z.infer<typeof planStatusSummarySchema>;

export const billingPlanListingSchema = z.object({
  results: z
    .array(billingPlanListItemSchema)
    .nullable()
    .transform((v) => v ?? []),
  totalCount: z.number(),
  statusSummary: planStatusSummarySchema,
  amountRange: billingPlanContractValueRangeSchema,
});

export const confirmBillingPlanResponseSchema = z.object({
  lineItemKey: z.string(),
  chargeMode: chargeModeSchema,
  activationDate: dateSchema.nullable(),
  chargesScheduled: z.number(),
});
